// --- React & Redux
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
// --- External UI Libraries
import { Col, Row } from 'antd';
import Button from '@dtci/spellbook/Button';
import Input from '@dtci/spellbook/Input';
import Select from '@dtci/spellbook/Select';
// --- Hooks & Actions
import { useJobsActions } from 'datastage/store/jobs/useJobs';
import { useJobsData } from '../../hooks/useJobsData';
// --- Components
import { InputLabel } from 'datastage/components/InputLabel';
// --- Utilities
import { datastageApi } from 'datastage/api';
import { getItemRange } from 'datastage/utils/pagination';
import { parseSearchParams, updateSearchParams } from 'datastage/utils/urlParams';
import { validateSpecialCharacters } from 'datastage/utils/validateSpecialCharacters';
import { cleanJobFilter, findStatusByValue, findTypeByValue } from './utils';
// --- Styles
import './styles.scss';

/**
 * Job Search Form
 *
 * Features:
 * - Renders a filter form for Jobs (Job ID, Job Type, Deal ID, Job Status, Start Date, End Date)
 * - Debounced auto-search on input change (400ms delay)
 * - Input validation for Job ID and Deal ID fields
 * - Date validation (start date must be before end date)
 * - Syncs filter state with URL params bidirectionally
 * - Reset button clears all filters
 *
 * @returns {JSX.Element}
 */
export function JobsSearchForm() {
    const navigate = useNavigate();
    const location = useLocation();
    const isInitialMount = useRef(true);
    const debounceTimerRef = useRef(null);

    const { fetchJobs, updateJobs, jobRange, jobSort, itemsPerPage } = useJobsData();
    const { updateJobsSearchForm, clearJobs } = useJobsActions();
    const { filterForm, jobStatusOptions, jobTypeOptions } = useSelector((s) => s.jobs);

    // Validation state for inline error messages
    const [validationErrors, setValidationErrors] = useState({
        jobId: undefined,
        dealId: undefined,
        dateRange: undefined,
    });

    // Fetch job status and type options if not already loaded
    useEffect(() => {
        if (
            !Array.isArray(jobStatusOptions) ||
            jobStatusOptions.length === 0 ||
            !Array.isArray(jobTypeOptions) ||
            jobTypeOptions.length === 0
        ) {
            (async () => {
                try {
                    const [statuses, types] = await Promise.all([
                        datastageApi.jobStatuses(false),
                        datastageApi.jobTypes(false),
                    ]);
                    updateJobs({
                        jobStatusOptions: statuses,
                        jobTypeOptions: types,
                    });
                } catch (error) {
                    console.error('Error fetching job options:', error);
                }
            })();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Sync URL params to form state on navigation (location.key changes on each navigation)
    useEffect(() => {
        const params = parseSearchParams(location.search);
        const filterData = buildFilterFromParams(params, jobTypeOptions, jobStatusOptions);

        // Handle pageSize from URL (TAP pattern)
        let rangeOverride;
        if (params.pageSize) {
            const newPageSize = parseInt(params.pageSize, 10);
            if (!isNaN(newPageSize) && newPageSize > 0) {
                rangeOverride = getItemRange(0, newPageSize);
                updateJobs({
                    itemsPerPage: newPageSize,
                    jobRange: rangeOverride,
                    currentPage: 0,
                });
            }
        }

        const hasUrlParams = Object.keys(filterData).length > 0;

        if (hasUrlParams) {
            updateJobsSearchForm({ key: 'filterForm', data: filterData });
        }

        // Merge URL-derived filters onto Redux filterForm so defaults apply when the URL
        // only carries partial params (e.g. pageSize).
        const filterForFetch = hasUrlParams ? { ...filterForm, ...filterData } : filterForm;

        fetchData(filterForFetch, rangeOverride);
        isInitialMount.current = false;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.key]);

    // Re-parse URL params when options load (needed for select values that depend on options)
    useEffect(() => {
        if (isInitialMount.current) return;
        if (!Array.isArray(jobTypeOptions) || jobTypeOptions.length === 0) return;
        if (!Array.isArray(jobStatusOptions) || jobStatusOptions.length === 0) return;

        const params = parseSearchParams(location.search);
        if (params.jobType || params.jobStatus) {
            const filterData = buildFilterFromParams(params, jobTypeOptions, jobStatusOptions);
            const mergedFilterData = { ...filterForm, ...filterData };
            updateJobsSearchForm({ key: 'filterForm', data: filterData });

            // Handle pageSize if present
            let rangeOverride;
            if (params.pageSize) {
                const newPageSize = parseInt(params.pageSize, 10);
                if (!isNaN(newPageSize) && newPageSize > 0) {
                    rangeOverride = getItemRange(0, newPageSize);
                }
            }

            fetchData(mergedFilterData, rangeOverride);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobTypeOptions, jobStatusOptions]);

    /**
     * Builds filter form data from URL search params.
     * Converts string params to appropriate types and matches select options.
     */
    function buildFilterFromParams(params, typeOptions, statusOptions) {
        const filterData = {};

        if (params.jobId) filterData.jobId = params.jobId;
        if (params.dealId) filterData.dealId = params.dealId;
        if (params.startDate) filterData.startDate = params.startDate;
        if (params.endDate) filterData.endDate = params.endDate;

        // Match select options (only if options are loaded)
        if (params.jobType && Array.isArray(typeOptions) && typeOptions.length > 0) {
            const matched = findTypeByValue(typeOptions, params.jobType);
            if (matched) filterData.jobType = matched;
        }

        if (params.jobStatus && Array.isArray(statusOptions) && statusOptions.length > 0) {
            const matched = findStatusByValue(statusOptions, params.jobStatus);
            if (matched) filterData.jobStatus = matched;
        }

        return filterData;
    }

    /**
     * Fetches jobs data and updates Redux state.
     * @param {Object} filterData - Filter criteria
     * @param {Array} rangeOverride - Optional range override (e.g., when pageSize changes)
     */
    const fetchData = async (filterData, rangeOverride) => {
        // Merge with current filterForm to include all fields
        const mergedFilter = { ...filterForm, ...filterData };
        const range = rangeOverride ?? getItemRange(0, itemsPerPage);
        const data = await fetchJobs(range, mergedFilter);
        if (data) {
            updateJobs({
                jobs: data?.content ?? [],
                totalPages: data?.totalPages ?? 0,
                currentPage: data?.pageable?.pageNumber ?? 0,
                totalItems: data?.totalElements ?? 0,
                jobRange: range,
            });
        }
    };

    /**
     * Reset navigates to clean URL; the location.key useEffect handles re-fetch.
     */
    const handleResetFilter = () => {
        clearJobs();
        setValidationErrors({ jobId: undefined, dealId: undefined, dateRange: undefined });
        navigate(location.pathname);
    };

    /**
     * Validate that start date is before end date.
     */
    function validateDates(startDate, endDate) {
        if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
            return 'Start Date must be before End Date';
        }
        return undefined;
    }

    /**
     * Handles input changes with validation and debounced search.
     * Validates ID fields for special characters and dates for range.
     * Triggers search after 400ms of no typing.
     */
    function handleInputChange(field, value) {
        let newValidationErrors = { ...validationErrors };

        // Validate ID fields for special characters
        if (field === 'jobId' || field === 'dealId') {
            const error = validateSpecialCharacters(value);
            newValidationErrors[field] = error;
        }

        // Validate date range
        if (field === 'startDate') {
            newValidationErrors.dateRange = validateDates(value, filterForm.endDate);
        } else if (field === 'endDate') {
            newValidationErrors.dateRange = validateDates(filterForm.startDate, value);
        }

        setValidationErrors(newValidationErrors);

        // Update form state
        const newFilterForm = { ...filterForm, [field]: value };
        updateJobsSearchForm({
            key: 'filterForm',
            data: { [field]: value },
        });

        // Debounce the search to avoid excessive API calls
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        debounceTimerRef.current = setTimeout(() => {
            const hasErrors = Object.values(newValidationErrors).some((error) => error);
            if (!hasErrors) {
                const firstPageRange = getItemRange(0, itemsPerPage);
                updateJobs({ jobRange: firstPageRange, currentPage: 0 });
                const filtered = cleanJobFilter(newFilterForm, firstPageRange, jobSort);
                updateSearchParams(location, navigate, filtered);
            }
        }, 400);
    }

    /**
     * Handles select changes with debounced search.
     */
    function handleSelectChange(field, value) {
        const newFilterForm = { ...filterForm, [field]: value };
        updateJobsSearchForm({
            key: 'filterForm',
            data: { [field]: value },
        });

        // Debounce the search to avoid excessive API calls
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        debounceTimerRef.current = setTimeout(() => {
            const hasErrors = Object.values(validationErrors).some((error) => error);
            if (!hasErrors) {
                const firstPageRange = getItemRange(0, itemsPerPage);
                updateJobs({ jobRange: firstPageRange, currentPage: 0 });
                const filtered = cleanJobFilter(newFilterForm, firstPageRange, jobSort);
                updateSearchParams(location, navigate, filtered);
            }
        }, 400);
    }

    return (
        <Row className="jobs-filter-form">
            <Col>
                <InputLabel colProps={{ span: 24 }} label="Job ID">
                    <Input
                        value={filterForm.jobId}
                        onChange={(e) => handleInputChange('jobId', e.target.value)}
                        width="auto"
                        id="jobId"
                        name="jobId"
                        placeholder="Enter"
                        className={validationErrors.jobId ? 'input-error' : ''}
                    />
                    {validationErrors.jobId && (
                        <div className="validation-error">{validationErrors.jobId}</div>
                    )}
                </InputLabel>
            </Col>
            <Col>
                <InputLabel colProps={{ span: 24 }} label="Job Types">
                    <Select
                        options={jobTypeOptions}
                        isClearable
                        isSearchable
                        onChange={(value) => handleSelectChange('jobType', value)}
                        value={filterForm.jobType}
                    />
                </InputLabel>
            </Col>
            {filterForm?.jobType?.id === 'DEAL_VERSION_CAPTURE' && (
                <Col>
                    <InputLabel colProps={{ span: 24 }} label="Deal ID">
                        <Input
                            value={filterForm.dealId}
                            onChange={(e) => handleInputChange('dealId', e.target.value)}
                            width="auto"
                            id="dealId"
                            name="dealId"
                            placeholder="Enter"
                            className={validationErrors.dealId ? 'input-error' : ''}
                        />
                        {validationErrors.dealId && (
                            <div className="validation-error">{validationErrors.dealId}</div>
                        )}
                    </InputLabel>
                </Col>
            )}
            <Col>
                <InputLabel colProps={{ span: 24 }} label="Job Status">
                    <Select
                        options={jobStatusOptions}
                        isClearable
                        isSearchable
                        onChange={(value) => handleSelectChange('jobStatus', value)}
                        value={filterForm.jobStatus}
                    />
                </InputLabel>
            </Col>
            <Col>
                <InputLabel colProps={{ span: 24 }} label="Start Date">
                    <Input
                        labelText="Date Input"
                        type="date"
                        value={filterForm.startDate}
                        onChange={(e) => handleInputChange('startDate', e.target.value)}
                        className={validationErrors.dateRange ? 'input-error' : ''}
                    />
                </InputLabel>
            </Col>
            <Col>
                <InputLabel colProps={{ span: 24 }} label="End Date">
                    <Input
                        labelText="Date Input"
                        type="date"
                        value={filterForm.endDate}
                        onChange={(e) => handleInputChange('endDate', e.target.value)}
                        className={validationErrors.dateRange ? 'input-error' : ''}
                    />
                    {validationErrors.dateRange && (
                        <div className="validation-error">{validationErrors.dateRange}</div>
                    )}
                </InputLabel>
            </Col>
            <Col>
                <Button
                    className="reset-button"
                    sizeType="small"
                    styleType="standard"
                    onClick={handleResetFilter}
                >
                    Reset
                </Button>
            </Col>
        </Row>
    );
}
